import { Button, Classes, Intent } from "@blueprintjs/core";
import { NiraDialog } from "components/common/NiraDialog";
import { CostAssumption, ScenarioStage } from "in_queue/types/scenarioType";
import { useState } from "react";
import { CustomUpgradePicker } from "../ScenarioDialog/AddUpgradesPanel";
import { CostAssumptionsPicker } from "../ScenarioDialog/CostAssumptionsPanel";
import { CustomUpgradeRiskBanner } from "../ScenarioDialog/CustomUpgradeRiskBanner";
import { scenarioCount } from "../ScenarioRerun/bulkRerunToast";
import css from "./BulkScenarioSettingDialogs.module.scss";
import { BulkScenarioUploadLabel } from "./bulkScenarioUploadLabels";

const ApplyToScenariosDialog: React.FC<{
    readonly title: string;
    readonly count: number;
    readonly onClose: () => void;
    readonly onApply: () => void;
    readonly bodyClassName?: string;
    readonly children: React.ReactNode;
}> = ({ title, count, onClose, onApply, bodyClassName, children }) => (
    <NiraDialog
        isOpen
        onClose={onClose}
        eyebrow={BulkScenarioUploadLabel.Eyebrow}
        title={title}
        className={css["BulkScenarioSettingDialogs"]}
        bodyClassName={bodyClassName}
        gap="large"
        footerActions={
            <>
                <Button text="Cancel" onClick={onClose} size="large" />
                <Button
                    text={`Apply to ${scenarioCount(count)}`}
                    intent={Intent.PRIMARY}
                    onClick={onApply}
                    size="large"
                />
            </>
        }
    >
        {children}
    </NiraDialog>
);

export const BulkCostAssumptionsDialog: React.FC<{
    readonly count: number;
    readonly scenarioStage: ScenarioStage;
    readonly initialValue: CostAssumption;
    readonly onClose: () => void;
    readonly onApply: (costAssumptions: CostAssumption) => void;
}> = ({ count, scenarioStage, initialValue, onClose, onApply }) => {
    const [costAssumptions, setCostAssumptions] = useState(initialValue);

    return (
        <ApplyToScenariosDialog
            title={BulkScenarioUploadLabel.SetCostAssumptions}
            count={count}
            onClose={onClose}
            onApply={() => onApply(costAssumptions)}
        >
            <CostAssumptionsPicker
                costAssumptions={costAssumptions}
                setCostAssumptions={setCostAssumptions}
                scenarioStage={scenarioStage}
            />
        </ApplyToScenariosDialog>
    );
};

export const BulkCustomUpgradeDialog: React.FC<{
    readonly count: number;
    readonly initialValue: string | null;
    readonly onClose: () => void;
    readonly onApply: (customUpgradeId: string | null) => void;
}> = ({ count, initialValue, onClose, onApply }) => {
    const [customUpgradeId, setCustomUpgradeId] = useState(initialValue);

    return (
        <ApplyToScenariosDialog
            title={BulkScenarioUploadLabel.SetCustomUpgrade}
            count={count}
            onClose={onClose}
            onApply={() => onApply(customUpgradeId)}
            bodyClassName={css["BulkScenarioSettingDialogs-upgrade-body"]}
        >
            <span className={Classes.TEXT_MUTED}>
                Apply with no upgrade selected to remove it from the selected
                scenarios.
            </span>
            <CustomUpgradePicker
                selectedUpgradeId={customUpgradeId}
                setSelectedUpgradeId={setCustomUpgradeId}
                target={
                    count === 1 ? "this scenario" : `these ${count} scenarios`
                }
            />
            <CustomUpgradeRiskBanner
                selectedUpgradeId={customUpgradeId}
                className={css["BulkScenarioSettingDialogs-risk-banner"]}
            />
        </ApplyToScenariosDialog>
    );
};
